/**
 * NotchesFrameworkPage — /n2/framework
 *
 * The "How the Notches Framework works" compact view within the /n2
 * navigation experience. Explains the three stages (Understand /
 * Evaluate / Execute) as a connected process, with direct links into each
 * stage's Level-2 branch inside the wizard.
 *
 * This is a public marketing surface. It does not claim every published
 * opportunity connects to an automated engine, does not fabricate
 * research examples, and does not present pricing tables. Keep concrete,
 * benefit-focused, and short.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { palette, type as typeTokens } from './theme';
import {
  NotchesExplainerFrame,
  useIsMobile,
  useReducedMotion,
} from './NotchesExplainerFrame';

type Stage = {
  numeral: string;
  label: string;
  headline: string;
  body: string;
  destination: string;
  destinationLabel: string;
};

const STAGES: Stage[] = [
  {
    numeral: '01',
    label: 'Understand',
    headline: 'Put market conditions into context.',
    body: 'Explore market structure, volatility, and research briefings before examining individual opportunities.',
    destination: '/n2?path=understand',
    destinationLabel: 'Explore Understand →',
  },
  {
    numeral: '02',
    label: 'Evaluate',
    headline: 'See the reasoning behind the opportunity.',
    body: 'Examine the supporting analysis, assumptions, and risk considerations behind published research.',
    destination: '/n2?path=evaluate',
    destinationLabel: 'Explore Evaluate →',
  },
  {
    numeral: '03',
    label: 'Execute',
    headline: 'Choose whether — and how — to act.',
    body: 'Place trades independently through your own broker or explore supported Notches automated trading engines.',
    destination: '/n2?path=execute',
    destinationLabel: 'Explore Execute →',
  },
];

export const NotchesFrameworkPage: React.FC = () => {
  const isMobile = useIsMobile();
  const reduced = useReducedMotion();

  return (
    <NotchesExplainerFrame
      breadcrumbTrail={[
        { label: 'Notches', to: '/n2' },
        { label: 'The Notches Framework' },
      ]}
    >
      <Hero reduced={reduced} isMobile={isMobile} />

      <Section title="Three stages, one connected process">
        <p style={paragraphStyle}>
          Notches brings market research, researched opportunities, and
          trading capabilities into one framework. Explore each stage
          independently or follow the process through.
        </p>
        <ol
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {STAGES.map((stage) => (
            <StageCard key={stage.numeral} stage={stage} />
          ))}
        </ol>
      </Section>

      <Section title="Research and methodology">
        <p style={paragraphStyle}>
          The public methodology page describes what each stage looks like
          in practice — how briefings are written, how researched
          opportunities are examined, and where the framework's boundaries
          are drawn.
        </p>
        <p style={paragraphStyle}>
          <Link to="/n2/methodology" style={inlineLinkStyle}>
            Read the methodology →
          </Link>
        </p>
      </Section>

      <Section title="Learn the framework with guidance">
        <p style={paragraphStyle}>
          The Boring Bucket is a mentored membership that spans the
          framework. Learn to apply Understand, Evaluate, and Execute
          through collective mentoring, session recordings, a capital
          allocation plan, the founder's real-money account view, and a
          member community.
        </p>
        <p style={paragraphStyle}>
          <Link to="/n2/boring-bucket" style={inlineLinkStyle}>
            Explore The Boring Bucket →
          </Link>
        </p>
      </Section>

      <Section title="Where to next">
        <div style={ctaRowStyle(isMobile)}>
          <CtaLink to="/n2" primary>
            ← Back to Notches
          </CtaLink>
          <CtaLink to="/n2?path=understand">Start with Understand →</CtaLink>
          <CtaLink to="/n2/manual-trading">Manual Trading →</CtaLink>
          <CtaLink to="/n2/automated-trading">Automated Trading →</CtaLink>
        </div>
      </Section>
    </NotchesExplainerFrame>
  );
};

// ─── Building blocks ─────────────────────────────────────────────────────

const Hero: React.FC<{ reduced: boolean; isMobile: boolean }> = ({
  reduced,
  isMobile,
}) => (
  <header
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      animation: reduced
        ? undefined
        : 'notches-explainer-fade-in 320ms ease-out both',
    }}
  >
    <span
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 12,
        fontWeight: 700,
        color: palette.ink3,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
      }}
    >
      The Notches Framework
    </span>
    <h1
      tabIndex={-1}
      style={{
        fontFamily: typeTokens.serif,
        fontSize: isMobile ? 32 : 40,
        fontWeight: 400,
        margin: 0,
        color: palette.ink,
        letterSpacing: '-0.01em',
        lineHeight: 1.15,
        textWrap: 'balance' as any,
        outline: 'none',
      }}
    >
      A connected process, from context to action.
    </h1>
    <p
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 16,
        color: palette.ink2,
        lineHeight: 1.5,
        margin: 0,
        maxWidth: 620,
      }}
    >
      Understand the market. Evaluate the opportunity. Choose whether — and
      how — to act. Each stage stands on its own; taken together they
      form the Notches Framework.
    </p>
  </header>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <h2
      style={{
        fontFamily: typeTokens.serif,
        fontSize: 24,
        fontWeight: 400,
        margin: 0,
        color: palette.ink,
        letterSpacing: '-0.005em',
        lineHeight: 1.2,
      }}
    >
      {title}
    </h2>
    {children}
  </section>
);

const StageCard: React.FC<{ stage: Stage }> = ({ stage }) => (
  <li
    style={{
      border: `1px solid ${palette.line}`,
      borderRadius: 2,
      padding: 20,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      background: palette.paper,
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 44,
          height: 44,
          borderRadius: '50%',
          border: `1.25px solid ${palette.gold}`,
          background: palette.paper,
          color: palette.goldDeep,
          fontFamily: typeTokens.sans,
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '0.05em',
          flexShrink: 0,
        }}
      >
        {stage.numeral}
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
        <span
          style={{
            fontFamily: typeTokens.sans,
            fontSize: 12,
            fontWeight: 700,
            color: palette.ink3,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
          }}
        >
          {stage.label}
        </span>
        <h3
          style={{
            fontFamily: typeTokens.serif,
            fontSize: 22,
            fontWeight: 400,
            margin: 0,
            color: palette.ink,
            lineHeight: 1.25,
            letterSpacing: '-0.005em',
            textWrap: 'balance' as any,
          }}
        >
          {stage.headline}
        </h3>
      </div>
    </div>
    <p
      style={{
        ...paragraphStyle,
        margin: 0,
        paddingLeft: 60,
      }}
    >
      {stage.body}
    </p>
    <div
      style={{
        paddingLeft: 60,
        paddingTop: 4,
      }}
    >
      <Link to={stage.destination} style={inlineLinkStyle}>
        {stage.destinationLabel}
      </Link>
    </div>
  </li>
);

const CtaLink: React.FC<{
  to: string;
  primary?: boolean;
  children: React.ReactNode;
}> = ({ to, primary, children }) => (
  <Link
    to={to}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '10px 16px',
      borderRadius: 2,
      textDecoration: 'none',
      fontFamily: typeTokens.sans,
      fontSize: 14,
      fontWeight: 600,
      color: primary ? palette.paper : palette.ink,
      background: primary ? palette.goldDeep : 'transparent',
      border: `1px solid ${primary ? palette.goldDeep : palette.line}`,
      transition: 'background 150ms ease-out, color 150ms ease-out',
    }}
  >
    {children}
  </Link>
);

const paragraphStyle: React.CSSProperties = {
  fontFamily: typeTokens.sans,
  fontSize: 15,
  color: palette.ink2,
  lineHeight: 1.6,
  margin: 0,
  maxWidth: 640,
};

const inlineLinkStyle: React.CSSProperties = {
  color: palette.goldDeep,
  textDecoration: 'underline',
  textDecorationColor: palette.line,
  textUnderlineOffset: 3,
  fontWeight: 600,
};

const ctaRowStyle = (isMobile: boolean): React.CSSProperties => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  gap: 12,
  flexWrap: 'wrap',
});
